/*
 * Books' reports, like QuickBooks' and Xero's: cash flow (first, the owner's pick, Nov 1), profit and loss (as a statement, by month, or next to last year), the
 * balance sheet, the general ledger, vendors, 1099s and profit by company. Any span: this month, last quarter, last
 * year or days the owner picks. Each line opens the transactions behind it, and each report downloads as CSV.
 */
import { useMemo, useState, type ReactNode } from "react";
import {
  CATEGORY_GROUPS,
  DIRECT_GROUPS,
  isOwed,
  payeeGuess,
  payeeKey,
  yearBefore,
  type BooksSetup,
  type CashFlowAnswer,
  type PnlAnswer,
  type TransactionRow,
} from "@steward/shared";
import {
  use1099,
  useBalance,
  useBookTransactions,
  useBrandPnl,
  useBulkCategory,
  useCashFlow,
  useChangeTransaction,
  useLedger,
  usePnl,
  useSavePayee,
} from "../../api/books";
import { useSession } from "../../app/session";
import { Change } from "../../components/Charts";
import { DataTable } from "../../components/DataTable";
import { InputText } from "../../components/fields";
import { money, moneyExact, monthName, plural, shortDate } from "../../components/format";
import { FilterPick, Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, EmptyState, LoadError, Skeleton, StatusLabel, Switch } from "../../components/parts";
import { CategoryPick } from "./CategoryPick";
import { KIND_WORDS, SpanPicker, Tile, downloadCsv, dollars, errText, pickList, useSpan, whole, type Span } from "./common";

export type Report = "cash" | "pnl" | "balance" | "ledger" | "vendors" | "c1099" | "brands";
type PnlView = "statement" | "months" | "compare";

export function Reports({
  setup,
  today,
  report,
  setReport,
}: {
  setup: BooksSetup;
  today: string;
  report: Report;
  setReport: (r: Report) => void;
}) {
  const { can } = useSession();
  const span = useSpan(today, "year");
  const list: [Report, string][] = [
    ["cash", "Cash flow"],
    ["pnl", "Profit and loss"],
    ["balance", "Balance sheet"],
    ["ledger", "General ledger"],
    ["vendors", "Vendors"],
    ["c1099", "1099s"],
    ...(can("report.children") ? ([["brands", "By company"]] as [Report, string][]) : []),
  ];
  return (
    <div className="bk-reports">
      <nav className="bk-report-nav" aria-label="Reports">
        {list.map(([v, label]) => (
          <button key={v} type="button" aria-current={report === v ? "page" : undefined} onClick={() => setReport(v)}>
            {label}
          </button>
        ))}
      </nav>
      <div className="bk-report-body">
        {report === "balance" ? null : report === "c1099" ? null : <SpanPicker s={span} today={today} />}
        {report === "cash" ? (
          <CashFlow setup={setup} span={span.span} />
        ) : report === "pnl" ? (
          <ProfitAndLoss setup={setup} span={span.span} />
        ) : report === "balance" ? (
          <BalanceSheet today={today} />
        ) : report === "ledger" ? (
          <GeneralLedger setup={setup} span={span.span} />
        ) : report === "vendors" ? (
          <Vendors setup={setup} span={span.span} />
        ) : report === "c1099" ? (
          <Contractors today={today} />
        ) : (
          <ByCompany span={span.span} />
        )}
      </div>
    </div>
  );
}

// Cash flow ----------------------------------------------------------------------------------------------------------

const CASH_TITLES: Record<CashFlowAnswer["sections"][number]["key"], string> = {
  in: "Money in",
  out: "Money out to run the business",
  kept: "Bought to keep",
  owner: "Owner, loans and cards",
};

function CashFlow({ setup, span }: { setup: BooksSetup; span: Span }) {
  const { company } = useSession();
  const [view, setView] = useState<"statement" | "months">("statement");
  const [drill, setDrill] = useState<{ categoryId: string; name: string } | null>(null);
  const q = useCashFlow(company.id, span.from, span.to);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  const c = q.data;
  if (!c) return <Skeleton rows={8} />;
  const months = view === "months";
  const moneyIn = c.sections.find((s) => s.key === "in")?.totalCents ?? 0;
  const moneyOut = moneyIn - c.netCents;
  const cells = (byMonth: (number | null)[], total: number | null, bold = false) => {
    const td = (v: number | null, k: string) => (
      <td key={k} className="num">
        {v === null ? "" : bold ? <b>{moneyExact(v)}</b> : moneyExact(v)}
      </td>
    );
    return months ? [...byMonth.map((v, i) => td(v, String(i))), td(total, "t")] : [td(total, "t")];
  };
  const exportCsv = () => {
    const head = months ? ["Line", ...c.months.map((m) => monthName(`${m}-01`)), "Total"] : ["Line", "Total"];
    const val = (v: number | null) => (v === null ? "" : dollars(v));
    const row = (name: string, byMonth: (number | null)[], total: number | null) =>
      months ? [name, ...byMonth.map(val), val(total)] : [name, val(total)];
    const out: (string | number | null)[][] = [[company.name], [`Cash flow, ${c.from} to ${c.to}`], [], head];
    out.push(row("Cash at the start", c.startByMonth, c.startCents));
    for (const s of c.sections) {
      out.push([CASH_TITLES[s.key]]);
      for (const r of s.rows) out.push(row(`  ${r.name}`, r.byMonth, r.totalCents));
      out.push(row(`Total ${CASH_TITLES[s.key].toLowerCase()}`, s.byMonth, s.totalCents));
    }
    out.push(row("Cash went up or down", c.netByMonth, c.netCents));
    out.push(row("Cash at the end", c.endByMonth, c.endCents));
    downloadCsv(`cash-flow-${c.from}-to-${c.to}.csv`, out);
  };
  return (
    <div className={`stack${q.isPlaceholderData ? " refreshing" : ""}`}>
      <div className="tiles">
        <Tile
          label="Cash at the start"
          value={c.startCents === null ? "Not set" : whole(c.startCents)}
          hint="Checking, savings and cash"
        />
        <Tile label="Money in" value={whole(moneyIn)} />
        <Tile label="Money out" value={whole(moneyOut)} hint="Costs, buys, draws, card bills" />
        <Tile
          label="Cash at the end"
          value={c.endCents === null ? "Not set" : whole(c.endCents)}
          hint={`${c.netCents >= 0 ? "Up" : "Down"} ${moneyExact(Math.abs(c.netCents))}`}
          tone={c.netCents >= 0 ? "up" : "down"}
        />
      </div>
      <Card
        title="Cash flow"
        action={
          <div className="row tight">
            <FilterPick<"statement" | "months">
              label="Show"
              value={view}
              onChange={setView}
              options={[
                { value: "statement", label: "Totals" },
                { value: "months", label: "By month" },
              ]}
            />
            <Button size="small" type="secondary" icon="file" onClick={exportCsv}>
              CSV
            </Button>
          </div>
        }
        flush
      >
        <p className="muted bk-pad">
          Money that went in and out of your checking, savings and cash. Card buys count when you pay the card bill.
        </p>
        <div className="bk-st-wrap">
          <table className="bk-st">
            <thead>
              <tr>
                <th />
                {months
                  ? c.months.map((m) => (
                      <th key={m} className="num">
                        {monthName(`${m}-01`).slice(0, 3)}
                      </th>
                    ))
                  : null}
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bk-st-total">
                <th scope="row">Cash at the start</th>
                {cells(c.startByMonth, c.startCents)}
              </tr>
            </tbody>
            {c.sections.map((s) => (
              <tbody key={s.key}>
                <tr className="bk-st-group">
                  <th colSpan={(months ? c.months.length : 0) + 2}>{CASH_TITLES[s.key]}</th>
                </tr>
                {s.rows.map((r) => (
                  <tr
                    key={r.categoryId ?? r.name}
                    className="bk-st-line"
                    onClick={() => {
                      if (r.categoryId) setDrill({ categoryId: r.categoryId, name: r.name });
                    }}
                  >
                    <th scope="row">
                      {r.categoryId ? (
                        <button type="button" className="linkish">
                          {r.name}
                        </button>
                      ) : (
                        r.name
                      )}
                    </th>
                    {cells(r.byMonth, r.totalCents)}
                  </tr>
                ))}
                <tr className="bk-st-total">
                  <th scope="row">Total</th>
                  {cells(s.byMonth, s.totalCents, true)}
                </tr>
              </tbody>
            ))}
            <tbody>
              <tr className="bk-st-total big">
                <th scope="row">Cash went up or down</th>
                {cells(c.netByMonth, c.netCents, true)}
              </tr>
              <tr className="bk-st-total big">
                <th scope="row">Cash at the end</th>
                {cells(c.endByMonth, c.endCents, true)}
              </tr>
            </tbody>
          </table>
        </div>
        {c.startCents === null ? (
          <p className="muted bk-pad">Set opening balances on your accounts in Accounts and rules to see cash on hand.</p>
        ) : null}
      </Card>
      {drill ? (
        <LedgerModal
          title={drill.name}
          categoryId={drill.categoryId}
          span={{ from: c.from, to: c.to }}
          setup={setup}
          onClose={() => setDrill(null)}
        />
      ) : null}
    </div>
  );
}

// Profit and loss ----------------------------------------------------------------------------------------------------

interface Line {
  categoryId: string;
  name: string;
  group: string;
  type: string;
  taxLine: string | null;
  now: number;
  before: number;
  byMonth: number[];
}

function linesOf(now: PnlAnswer, before: PnlAnswer | undefined): Line[] {
  const all = new Map<string, Line>();
  for (const r of now.rows) all.set(r.categoryId, { ...r, now: r.totalCents, before: 0, byMonth: r.byMonth });
  for (const r of before?.rows ?? []) {
    const had = all.get(r.categoryId);
    if (had) had.before = r.totalCents;
    else all.set(r.categoryId, { ...r, now: 0, before: r.totalCents, byMonth: now.months.map(() => 0) });
  }
  return [...all.values()];
}

function ProfitAndLoss({ setup, span }: { setup: BooksSetup; span: Span }) {
  const { company } = useSession();
  const [view, setView] = useState<PnlView>("statement");
  const prev = yearBefore(span);
  const q = usePnl(company.id, span.from, span.to);
  const p0 = usePnl(company.id, prev.from, prev.to);
  const [drill, setDrill] = useState<{ categoryId: string; name: string } | null>(null);
  if (q.error) return <LoadError error={q.error} retry={() => void q.refetch()} />;
  const p = q.data;
  if (!p) return <Skeleton rows={8} />;
  const compare = view === "compare";
  const before = compare ? p0.data : undefined;
  const lines = linesOf(p, before);
  const groupOrder = Object.values(CATEGORY_GROUPS).flat();
  const byGroup = (types: string[], groups?: readonly string[], not?: readonly string[]) =>
    groupOrder
      .filter((g) => (!groups || groups.includes(g)) && !(not ?? []).includes(g))
      .map((g) => ({
        group: g,
        lines: lines.filter((l) => types.includes(l.type) && l.group === g).sort((a, b) => b.now - a.now || b.before - a.before),
      }))
      .filter((g) => g.lines.length);
  const sum = (ls: Line[], k: "now" | "before") => ls.reduce((a, l) => a + l[k], 0);
  const sumMonths = (ls: Line[]) => p.months.map((_, i) => ls.reduce((a, l) => a + (l.byMonth[i] ?? 0), 0));
  const income = lines.filter((l) => l.type === "income");
  const direct = lines.filter((l) => l.type === "expense" && DIRECT_GROUPS.includes(l.group));
  const other = lines.filter((l) => l.type === "expense" && !DIRECT_GROUPS.includes(l.group));
  const kept = lines.filter((l) => l.type === "asset");
  const months = view === "months";
  const cols = months ? p.months.length + 1 : compare ? 4 : 1;
  const cells = (now: number, beforeCents: number, monthly: number[], strong = false, lowerIsBetter = false) => {
    const N = strong ? "b" : "span";
    if (months)
      return [
        ...monthly.map((c, i) => (
          <td key={i} className="num">
            {c ? money(c) : ""}
          </td>
        )),
        <td key="t" className="num">
          <N>{money(now)}</N>
        </td>,
      ];
    if (compare)
      return [
        <td key="n" className="num">
          <N>{money(now)}</N>
        </td>,
        <td key="b" className="num muted">
          {money(beforeCents)}
        </td>,
        <td key="d" className="num">
          {money(now - beforeCents)}
        </td>,
        <td key="c" className="bk-chg">
          {beforeCents ? <Change now={now} before={beforeCents} lowerIsBetter={lowerIsBetter} /> : null}
        </td>,
      ];
    return [
      <td key="n" className="num">
        <N>{money(now)}</N>
      </td>,
    ];
  };
  const section = (title: string, groups: { group: string; lines: Line[] }[], lowerIsBetter: boolean) =>
    groups.map((g) => (
      <tbody key={`${title}-${g.group}`}>
        {groups.length > 1 || g.group !== title ? (
          <tr className="bk-st-group">
            <th colSpan={cols + 1}>{g.group}</th>
          </tr>
        ) : null}
        {g.lines.map((l) => (
          <tr key={l.categoryId} className="bk-st-line" onClick={() => setDrill({ categoryId: l.categoryId, name: l.name })}>
            <th scope="row">
              <button type="button" className="linkish">
                {l.name}
              </button>
              {l.taxLine ? <span className="muted small"> line {l.taxLine}</span> : null}
            </th>
            {cells(l.now, l.before, l.byMonth, false, lowerIsBetter)}
          </tr>
        ))}
      </tbody>
    ));
  const total = (label: string, ls: Line[], lowerIsBetter = false, big = false) => (
    <tbody>
      <tr className={big ? "bk-st-total big" : "bk-st-total"}>
        <th scope="row">{label}</th>
        {cells(sum(ls, "now"), sum(ls, "before"), sumMonths(ls), true, lowerIsBetter)}
      </tr>
    </tbody>
  );
  const net = (label: string, plus: Line[], minus: Line[], big: boolean) => {
    const m1 = sumMonths(plus);
    const m2 = sumMonths(minus);
    return (
      <tbody>
        <tr className={big ? "bk-st-total big" : "bk-st-total"}>
          <th scope="row">{label}</th>
          {cells(
            sum(plus, "now") - sum(minus, "now"),
            sum(plus, "before") - sum(minus, "before"),
            m1.map((v, i) => v - (m2[i] ?? 0)),
            true,
          )}
        </tr>
      </tbody>
    );
  };
  const exportCsv = () => {
    const head = months
      ? ["Line", ...p.months.map((m) => monthName(`${m}-01`)), "Total"]
      : compare
        ? ["Line", "Now", "Last year", "Change"]
        : ["Line", "Total"];
    const row = (name: string, now: number, b: number, monthly: number[]) =>
      months
        ? [name, ...monthly.map(dollars), dollars(now)]
        : compare
          ? [name, dollars(now), dollars(b), dollars(now - b)]
          : [name, dollars(now)];
    const out: (string | number | null)[][] = [[company.name], [`Profit and loss, ${p.from} to ${p.to}`], [], head];
    const add = (ls: Line[]) => ls.forEach((l) => out.push(row(`  ${l.name}`, l.now, l.before, l.byMonth)));
    add(income);
    out.push(row("Total income", sum(income, "now"), sum(income, "before"), sumMonths(income)));
    add(direct);
    out.push(row("Gross profit", sum(income, "now") - sum(direct, "now"), sum(income, "before") - sum(direct, "before"), []));
    add(other);
    out.push(
      row(
        "Net profit",
        sum(income, "now") - sum(direct, "now") - sum(other, "now"),
        sum(income, "before") - sum(direct, "before") - sum(other, "before"),
        [],
      ),
    );
    add(kept);
    downloadCsv(`profit-and-loss-${p.from}-to-${p.to}.csv`, out);
  };
  return (
    <div className={`stack${q.isPlaceholderData ? " refreshing" : ""}`}>
      {p.uncategorizedCount ? (
        <Banner type="warning">
          {p.uncategorizedCount === 1 ? "1 transaction still needs" : `${String(p.uncategorizedCount)} transactions still need`} a
          category ({moneyExact(p.uncategorizedCents)}). They are not in profit until they have one.
        </Banner>
      ) : null}
      <div className="tiles">
        <Tile label="Income" value={whole(p.incomeCents)} />
        <Tile label="Gross profit" value={whole(p.grossCents)} hint="Less labor and job costs" />
        <Tile
          label="Net profit"
          value={whole(p.profitCents)}
          hint={p.incomeCents ? `${String(Math.round((p.profitCents / p.incomeCents) * 100))}% of income` : "Less every cost"}
        />
        <Tile label="Bought to keep" value={whole(p.boughtToKeepCents)} hint="Not a cost in profit" />
      </div>
      <Card
        title="Profit and loss"
        action={
          <div className="row tight">
            <FilterPick<PnlView>
              label="Show"
              value={view}
              onChange={setView}
              options={[
                { value: "statement", label: "Totals" },
                { value: "months", label: "By month" },
                { value: "compare", label: "Next to last year" },
              ]}
            />
            <Button size="small" type="secondary" icon="file" onClick={exportCsv}>
              CSV
            </Button>
          </div>
        }
        flush
      >
        <div className="bk-st-wrap">
          <table className="bk-st">
            <thead>
              <tr>
                <th />
                {months ? (
                  <>
                    {p.months.map((m) => (
                      <th key={m} className="num">
                        {monthName(`${m}-01`).slice(0, 3)}
                      </th>
                    ))}
                    <th className="num">Total</th>
                  </>
                ) : compare ? (
                  <>
                    <th className="num">
                      {shortDate(p.from)} to {shortDate(p.to)}
                    </th>
                    <th className="num">A year before</th>
                    <th className="num">Change</th>
                    <th />
                  </>
                ) : (
                  <th className="num">Total</th>
                )}
              </tr>
            </thead>
            {section("Income", byGroup(["income"]), false)}
            {total("Total income", income)}
            {direct.length ? section("Direct costs", byGroup(["expense"], DIRECT_GROUPS), true) : null}
            {direct.length ? total("Total direct costs", direct, true) : null}
            {net("Gross profit", income, direct, false)}
            {section("Costs", byGroup(["expense"], undefined, DIRECT_GROUPS), true)}
            {total("Total other costs", other, true)}
            {net("Net profit", income, [...direct, ...other], true)}
            {kept.length ? section("Bought to keep", byGroup(["asset"]), false) : null}
          </table>
        </div>
      </Card>
      {drill ? (
        <LedgerModal
          title={drill.name}
          categoryId={drill.categoryId}
          span={{ from: p.from, to: p.to }}
          setup={setup}
          onClose={() => setDrill(null)}
        />
      ) : null}
    </div>
  );
}

/** The lines behind one report line: every transaction and journal entry in that category over the span. */
function LedgerModal({
  title,
  categoryId,
  span,
  setup,
  onClose,
}: {
  title: string;
  categoryId: string;
  span: Span;
  setup: BooksSetup;
  onClose: () => void;
}) {
  const { company } = useSession();
  const q = useLedger(company.id, span.from, span.to, categoryId);
  const change = useChangeTransaction();
  const bulk = useBulkCategory();
  const toast = useToast();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [moveTo, setMoveTo] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const accts = new Map(setup.accounts.map((a) => [a.id, a.name]));
  const income = setup.categories.find((c) => c.id === categoryId)?.type === "income";
  const lines = q.data?.lines ?? [];
  const shown = (c: number) => (income ? -c : c);
  const sum = lines.reduce((a, l) => a + shown(l.amountCents), 0);
  // Past lines can be moved to another category here (Nov 2: some owner draws were really 1099 pay). Journal entries are
  // changed in Year end, so only bank lines can be picked.
  const txIds = (keys: Iterable<string>) =>
    lines.filter((l) => l.sourceType === "bank_tx" && [...keys].includes(l.id)).map((l) => l.sourceId);
  const move = () => {
    const ids = txIds(picked);
    if (!ids.length || !moveTo) return;
    bulk.mutate(
      { ids, categoryId: moveTo, ...(paidTo.trim() ? { payee: paidTo.trim() } : {}) },
      {
        onSuccess: (r) => {
          setPicked(new Set());
          const to = setup.categories.find((c) => c.id === moveTo)?.name ?? "";
          const left = ids.length - r.changed;
          toast(
            `Moved ${plural(r.changed, "line")} to ${to}${left ? `. ${plural(left, "line")} paired or matched stayed, open each to change it` : ""}.`,
          );
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  };
  return (
    <Modal title={title} open onClose={onClose} size="large">
      <p className="muted">
        {shortDate(span.from)} to {shortDate(span.to)}, {plural(lines.length, "line")}, {moneyExact(sum)} in all. Pick another
        category on a line to move it, or tick lines and move them together.
      </p>
      <DataTable
        title="Lines"
        rows={lines}
        rowKey={(l) => l.id}
        loading={q.isLoading}
        search={(l) => `${l.description} ${l.payee ?? ""}`}
        selected={picked}
        onSelect={setPicked}
        bulk={
          <div className="row tight bk-move">
            <CategoryPick categories={setup.categories} value={moveTo} onPick={setMoveTo} label="Category for the ticked ones" />
            <InputText
              label="Paid to"
              placeholder="Paid to, for 1099s"
              value={paidTo}
              maxLength={80}
              onChange={(e) => setPaidTo(e.target.value)}
            />
            <Button size="small" loading={bulk.isPending} disabled={!moveTo} onClick={move}>
              Move {plural(txIds(picked).length, "line")}
            </Button>
          </div>
        }
        columns={[
          { key: "d", header: "Day", sort: (l) => l.day, render: (l) => shortDate(l.day) },
          {
            key: "w",
            header: "What",
            render: (l) => (
              <span className="bk-desc">
                <b>{l.description}</b>
                <span className="muted small">
                  {l.sourceType === "adjust" ? "Journal entry" : (accts.get(l.bankAccountId ?? "") ?? "")}
                  {l.payee ? `, paid to ${l.payee}` : ""}
                </span>
              </span>
            ),
          },
          {
            key: "c",
            header: "Category",
            render: (l) =>
              l.sourceType === "bank_tx" ? (
                <Stop>
                  <CategoryPick
                    categories={setup.categories}
                    value={categoryId}
                    onPick={(v) => {
                      if (!v || v === categoryId) return;
                      change.mutate(
                        { id: l.sourceId, categoryId: v },
                        {
                          onSuccess: () => toast(`Moved to ${setup.categories.find((c) => c.id === v)?.name ?? ""}`),
                          onError: (e) => toast(errText(e), { kind: "error" }),
                        },
                      );
                    }}
                    label={`Category for ${l.description}`}
                  />
                </Stop>
              ) : (
                <span className="muted small">Journal entry</span>
              ),
          },
          {
            key: "m",
            header: "Amount",
            align: "right",
            sort: (l) => shown(l.amountCents),
            render: (l) => <span className="num">{moneyExact(shown(l.amountCents))}</span>,
          },
        ]}
      />
    </Modal>
  );
}

/** Keeps a click in the picker from reaching the row. */
function Stop({ children }: { children: ReactNode }) {
  return (
    <span className="bk-stop" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      {children}
    </span>
  );
}

// Balance sheet ------------------------------------------------------------------------------------------------------

function BalanceSheet({ today }: { today: string }) {
  const { company } = useSession();
  const [day, setDay] = useState(today);
  const q = useBalance(company.id, day);
  const b = q.data;
  const exportCsv = () => {
    if (!b) return;
    downloadCsv(`balance-sheet-${b.day}.csv`, [
      [company.name],
      [`Balance sheet on ${b.day}`],
      [],
      ["What the business has"],
      ...b.accounts
        .filter((a) => !a.personal && !isOwed(a.kind))
        .map((a) => [`  ${a.name}`, a.balanceCents === null ? "" : dollars(a.balanceCents)]),
      ["  Vehicles and equipment kept", dollars(b.boughtToKeepCents)],
      ["Total", dollars(b.cashCents + b.boughtToKeepCents)],
      [],
      ["What the business owes"],
      ...b.accounts
        .filter((a) => !a.personal && isOwed(a.kind))
        .map((a) => [`  ${a.name}`, a.balanceCents === null ? "" : dollars(-a.balanceCents)]),
      ["  Loans", dollars(b.loansOwedCents)],
      ["Total", dollars(b.cardsOwedCents + b.loansOwedCents)],
      [],
      ["Owner's share", dollars(b.equityCents)],
      ["  Owner put in, all time", dollars(b.ownerInCents)],
      ["  Owner took out, all time", dollars(-b.ownerOutCents)],
    ]);
  };
  return (
    <div className="stack">
      <div className="row tight">
        <InputText label="On the day" type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} />
      </div>
      {q.error ? (
        <LoadError error={q.error} />
      ) : !b ? (
        <Skeleton rows={6} />
      ) : (
        <>
          <div className="tiles">
            <Tile label="What it has" value={whole(b.cashCents + b.boughtToKeepCents)} hint="Money and things kept" />
            <Tile label="What it owes" value={whole(b.cardsOwedCents + b.loansOwedCents)} hint="Cards and loans" />
            <Tile label="Owner's share" value={whole(b.equityCents)} hint="What is left for the owner" />
          </div>
          <Card
            title={`Balance sheet on ${shortDate(b.day)}`}
            flush
            action={
              <Button size="small" type="secondary" icon="file" onClick={exportCsv}>
                CSV
              </Button>
            }
          >
            <div className="bk-st-wrap">
              <table className="bk-st">
                <tbody>
                  <tr className="bk-st-group">
                    <th colSpan={2}>What the business has</th>
                  </tr>
                  {b.accounts
                    .filter((a) => !a.personal && !isOwed(a.kind))
                    .map((a) => (
                      <tr key={a.id} className="bk-st-line">
                        <th scope="row">
                          {a.name} <span className="muted small">{KIND_WORDS[a.kind]}</span>
                        </th>
                        <td className="num">
                          {a.balanceCents === null ? (
                            <span className="muted small">Set opening balance</span>
                          ) : (
                            moneyExact(a.balanceCents)
                          )}
                        </td>
                      </tr>
                    ))}
                  <tr className="bk-st-line">
                    <th scope="row">Vehicles and equipment kept</th>
                    <td className="num">{moneyExact(b.boughtToKeepCents)}</td>
                  </tr>
                  <tr className="bk-st-total">
                    <th scope="row">Total</th>
                    <td className="num">
                      <b>{moneyExact(b.cashCents + b.boughtToKeepCents)}</b>
                    </td>
                  </tr>
                </tbody>
                <tbody>
                  <tr className="bk-st-group">
                    <th colSpan={2}>What the business owes</th>
                  </tr>
                  {b.accounts
                    .filter((a) => !a.personal && isOwed(a.kind))
                    .map((a) => (
                      <tr key={a.id} className="bk-st-line">
                        <th scope="row">{a.name}</th>
                        <td className="num">
                          {a.balanceCents === null ? (
                            <span className="muted small">Set opening balance</span>
                          ) : (
                            moneyExact(-a.balanceCents)
                          )}
                        </td>
                      </tr>
                    ))}
                  <tr className="bk-st-line">
                    <th scope="row">Loans</th>
                    <td className="num">{moneyExact(b.loansOwedCents)}</td>
                  </tr>
                  <tr className="bk-st-total">
                    <th scope="row">Total</th>
                    <td className="num">
                      <b>{moneyExact(b.cardsOwedCents + b.loansOwedCents)}</b>
                    </td>
                  </tr>
                </tbody>
                <tbody>
                  <tr className="bk-st-total big">
                    <th scope="row">Owner's share</th>
                    <td className="num">
                      <b>{moneyExact(b.equityCents)}</b>
                    </td>
                  </tr>
                  <tr className="bk-st-line">
                    <th scope="row">Owner put in, all time</th>
                    <td className="num muted">{moneyExact(b.ownerInCents)}</td>
                  </tr>
                  <tr className="bk-st-line">
                    <th scope="row">Owner took out, all time</th>
                    <td className="num muted">{moneyExact(-b.ownerOutCents)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// General ledger -----------------------------------------------------------------------------------------------------

function GeneralLedger({ setup, span }: { setup: BooksSetup; span: Span }) {
  const { company } = useSession();
  const [categoryId, setCategoryId] = useState("");
  const q = useLedger(company.id, span.from, span.to, categoryId || undefined);
  const cats = new Map(setup.categories.map((c) => [c.id, c]));
  const accts = new Map(setup.accounts.map((a) => [a.id, a.name]));
  const lines = q.data?.lines ?? [];
  const exportCsv = () =>
    downloadCsv(`general-ledger-${span.from}-to-${span.to}.csv`, [
      ["Day", "Category", "Schedule C line", "What", "Paid to", "Account", "Debit", "Credit"],
      ...lines.map((l) => [
        l.day,
        cats.get(l.accountId)?.name ?? "",
        cats.get(l.accountId)?.taxLine ?? "",
        l.description,
        l.payee,
        l.sourceType === "adjust" ? "Journal entry" : (accts.get(l.bankAccountId ?? "") ?? ""),
        l.amountCents > 0 ? dollars(l.amountCents) : "",
        l.amountCents < 0 ? dollars(-l.amountCents) : "",
      ]),
    ]);
  return (
    <>
      {q.data?.more ? <Banner type="notice">Only the first 10,000 lines show. Pick fewer days or one category.</Banner> : null}
      <DataTable
        title="General ledger"
        rows={lines}
        rowKey={(l) => l.id}
        loading={q.isLoading}
        search={(l) => `${l.description} ${l.payee ?? ""} ${cats.get(l.accountId)?.name ?? ""}`}
        tools={
          <div className="row tight">
            <FilterPick<string>
              label="Category"
              value={categoryId}
              onChange={setCategoryId}
              options={[{ value: "", label: "All" }, ...pickList(setup.categories)]}
            />
            <Button size="small" type="secondary" icon="file" onClick={exportCsv} disabled={!lines.length}>
              CSV
            </Button>
          </div>
        }
        empty={<EmptyState icon="books" title="Nothing in these days" />}
        columns={[
          { key: "d", header: "Day", sort: (l) => l.day, render: (l) => shortDate(l.day) },
          {
            key: "c",
            header: "Category",
            sort: (l) => cats.get(l.accountId)?.name ?? "",
            render: (l) => cats.get(l.accountId)?.name ?? "",
          },
          {
            key: "w",
            header: "What",
            render: (l) => (
              <span className="bk-desc">
                <b>{l.description}</b>
                <span className="muted small">
                  {l.sourceType === "adjust" ? "Journal entry" : (accts.get(l.bankAccountId ?? "") ?? "")}
                  {l.payee ? `, paid to ${l.payee}` : ""}
                </span>
              </span>
            ),
          },
          {
            key: "dr",
            header: "Debit",
            align: "right",
            sort: (l) => l.amountCents,
            render: (l) => (l.amountCents > 0 ? <span className="num">{moneyExact(l.amountCents)}</span> : null),
          },
          {
            key: "cr",
            header: "Credit",
            align: "right",
            sort: (l) => -l.amountCents,
            render: (l) => (l.amountCents < 0 ? <span className="num">{moneyExact(-l.amountCents)}</span> : null),
          },
        ]}
      />
    </>
  );
}

// Vendors ------------------------------------------------------------------------------------------------------------

/** Who a payment went to: the payee typed, else read from the bank's words, else the words without their numbers. */
function vendorName(t: TransactionRow): string {
  if (t.payee) return t.payee;
  const guess = payeeGuess(t.description);
  if (guess !== "") return guess;
  return t.description
    .replace(/\s*[#*]?\d[\d\s#*-]*$/, "")
    .trim()
    .slice(0, 40);
}

interface Vendor {
  key: string;
  name: string;
  paidCents: number;
  count: number;
  last: string;
  topCategory: string;
}

function Vendors({ setup, span }: { setup: BooksSetup; span: Span }) {
  const { company } = useSession();
  const q = useBookTransactions(company.id, span);
  const cats = new Map(setup.categories.map((c) => [c.id, c]));
  const [open, setOpen] = useState<Vendor | null>(null);
  const vendors = useMemo(() => {
    const by = new Map<string, Vendor & { cats: Map<string, number> }>();
    for (const t of q.data?.transactions ?? []) {
      if (t.amountCents >= 0) continue;
      const parts = t.split ?? [{ categoryId: t.categoryId ?? "", amountCents: t.amountCents }];
      // Moving money between our own accounts, card payments, loans and draws are not paid to a vendor.
      if (parts.every((p) => cats.get(p.categoryId)?.type === "other")) continue;
      const name = vendorName(t);
      const key = payeeKey(name);
      const v = by.get(key) ?? { key, name, paidCents: 0, count: 0, last: "", topCategory: "", cats: new Map() };
      v.paidCents -= t.amountCents;
      v.count++;
      if (t.day > v.last) v.last = t.day;
      for (const p of parts) if (p.categoryId) v.cats.set(p.categoryId, (v.cats.get(p.categoryId) ?? 0) - p.amountCents);
      by.set(key, v);
    }
    return [...by.values()].map(({ cats: m, ...v }) => {
      const top = [...m].sort((a, b) => b[1] - a[1])[0];
      return { ...v, topCategory: top ? (cats.get(top[0])?.name ?? "") : "Needs a category" };
    });
  }, [q.data, cats]);
  const rows = q.data?.transactions ?? [];
  const exportCsv = () =>
    downloadCsv(`vendors-${span.from}-to-${span.to}.csv`, [
      ["Vendor", "Paid", "Payments", "Last paid", "Mostly"],
      ...vendors.map((v) => [v.name, dollars(v.paidCents), v.count, v.last, v.topCategory]),
    ]);
  return (
    <>
      <DataTable
        title="Vendors"
        rows={vendors}
        rowKey={(v) => v.key || "none"}
        loading={q.isLoading}
        search={(v) => `${v.name} ${v.topCategory}`}
        onRowClick={setOpen}
        tools={
          <Button size="small" type="secondary" icon="file" onClick={exportCsv} disabled={!vendors.length}>
            CSV
          </Button>
        }
        empty={<EmptyState icon="team" title="No money paid out in these days" />}
        columns={[
          { key: "n", header: "Paid to", sort: (v) => v.name, render: (v) => <b>{v.name}</b> },
          { key: "c", header: "Mostly", sort: (v) => v.topCategory, render: (v) => v.topCategory },
          {
            key: "k",
            header: "Payments",
            align: "right",
            sort: (v) => v.count,
            render: (v) => <span className="num">{v.count}</span>,
          },
          { key: "l", header: "Last paid", sort: (v) => v.last, render: (v) => shortDate(v.last) },
          {
            key: "p",
            header: "Paid",
            align: "right",
            sort: (v) => v.paidCents,
            render: (v) => <b className="num">{moneyExact(v.paidCents)}</b>,
          },
        ]}
      />
      {open ? (
        <Modal title={open.name} open onClose={() => setOpen(null)} size="large">
          <p className="muted">
            {moneyExact(open.paidCents)} paid in {plural(open.count, "payment")}
          </p>
          <DataTable
            title="Payments"
            rows={rows.filter((t) => t.amountCents < 0 && payeeKey(vendorName(t)) === open.key)}
            rowKey={(t) => t.id}
            columns={[
              { key: "d", header: "Day", render: (t) => shortDate(t.day) },
              { key: "w", header: "What the bank says", render: (t) => t.description },
              {
                key: "c",
                header: "Category",
                render: (t) => (t.split ? "Split" : t.categoryId ? (cats.get(t.categoryId)?.name ?? "") : "Needs a category"),
              },
              {
                key: "m",
                header: "Amount",
                align: "right",
                render: (t) => <span className="num">{moneyExact(-t.amountCents)}</span>,
              },
            ]}
          />
        </Modal>
      ) : null}
    </>
  );
}

// 1099s --------------------------------------------------------------------------------------------------------------

function Contractors({ today }: { today: string }) {
  const { company } = useSession();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const q = use1099(company.id, year);
  const save = useSavePayee();
  const r = q.data;
  const exportCsv = () =>
    r &&
    downloadCsv(`1099s-${String(year)}.csv`, [
      ["Paid to", "Total", "Payments", "1099-NEC owed", "W-9 on file", "On the team"],
      ...r.payees.map((p) => [
        p.name,
        dollars(p.totalCents),
        p.count,
        p.reaches ? "yes" : "no",
        p.w9 ? "yes" : "no",
        p.personName,
      ]),
    ]);
  return (
    <>
      <div className="row tight">
        <FilterPick<string>
          label="Year"
          value={String(year)}
          onChange={(v) => setYear(Number(v))}
          options={[0, 1, 2].map((n) => ({
            value: String(Number(today.slice(0, 4)) - n),
            label: String(Number(today.slice(0, 4)) - n),
          }))}
        />
      </div>
      {q.error ? (
        <LoadError error={q.error} retry={() => void q.refetch()} />
      ) : !r ? (
        <Skeleton rows={4} />
      ) : (
        <>
          <p className="muted">
            Everyone paid from Contract labor in {year}. A 1099-NEC is owed to each one paid {money(r.lineCents)} or more, sent by
            January 31. Keep a W-9 for each. No tax ID numbers are kept here.
          </p>
          <DataTable
            title="Contractors"
            rows={r.payees}
            rowKey={(p) => p.key || "none"}
            tools={
              <Button size="small" type="secondary" icon="file" onClick={exportCsv} disabled={!r.payees.length}>
                CSV
              </Button>
            }
            empty={<EmptyState icon="team" title="Nobody paid from Contract labor this year" />}
            columns={[
              { key: "n", header: "Paid to", render: (p) => <b>{p.name}</b> },
              {
                key: "t",
                header: "Total",
                align: "right",
                sort: (p) => p.totalCents,
                render: (p) => <span className="num">{moneyExact(p.totalCents)}</span>,
              },
              { key: "c", header: "Payments", align: "right", render: (p) => <span className="num">{p.count}</span> },
              {
                key: "r",
                header: "1099",
                render: (p) =>
                  p.reaches ? (
                    <StatusLabel status="warning">1099 owed</StatusLabel>
                  ) : (
                    <StatusLabel status="inactive">Under the line</StatusLabel>
                  ),
              },
              { key: "p", header: "On the team", render: (p) => p.personName ?? "" },
              {
                key: "w",
                header: "W-9 on file",
                render: (p) =>
                  p.key ? (
                    <Switch
                      checked={p.w9}
                      onChange={(v) => void save.mutateAsync({ key: p.key, w9: v })}
                      label={`W-9 on file for ${p.name}`}
                    />
                  ) : (
                    "Name who was paid"
                  ),
              },
            ]}
          />
        </>
      )}
    </>
  );
}

// By company ---------------------------------------------------------------------------------------------------------

function ByCompany({ span }: { span: Span }) {
  const { company } = useSession();
  const q = useBrandPnl(company.id, span.from, span.to, true);
  const rows = q.data ?? [];
  return (
    <DataTable
      title="Profit by company"
      rows={rows}
      rowKey={(r) => r.companyId}
      loading={q.isLoading}
      tools={
        <Button
          size="small"
          type="secondary"
          icon="file"
          disabled={!rows.length}
          onClick={() =>
            downloadCsv(`profit-by-company-${span.from}-to-${span.to}.csv`, [
              ["Company", "Income", "Costs", "Profit"],
              ...rows.map((r) => [r.name, dollars(r.incomeCents), dollars(r.expenseCents), dollars(r.profitCents)]),
            ])
          }
        >
          CSV
        </Button>
      }
      columns={[
        { key: "n", header: "Company", render: (r) => <b>{r.name}</b> },
        {
          key: "i",
          header: "Income",
          align: "right",
          sort: (r) => r.incomeCents,
          render: (r) => <span className="num">{money(r.incomeCents)}</span>,
        },
        {
          key: "c",
          header: "Costs",
          align: "right",
          sort: (r) => r.expenseCents,
          render: (r) => <span className="num">{money(r.expenseCents)}</span>,
        },
        {
          key: "p",
          header: "Profit",
          align: "right",
          sort: (r) => r.profitCents,
          render: (r) => <b className="num">{money(r.profitCents)}</b>,
        },
      ]}
    />
  );
}
